import { useEffect, useState } from "react";
import { Check, X, Receipt, Download } from "lucide-react";
import { toast } from "sonner";
import api, { fcfa, getToken, API } from "@/lib/api";
import { Button } from "@/components/ui/button";

const STATUSES = [
  { key: "", label: "Toutes" },
  { key: "en_attente_paiement", label: "En attente" },
  { key: "payee", label: "Payées" },
  { key: "annulee", label: "Annulées" },
];
const BADGE = {
  en_attente_paiement: "bg-amber-100 text-amber-700",
  payee: "bg-green-100 text-green-700",
  annulee: "bg-red-100 text-red-700",
};
const LABEL = { en_attente_paiement: "En attente", payee: "Payée", annulee: "Annulée" };

export default function BookingsAdmin() {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("");

  const load = () => {
    const params = filter ? { status: filter } : {};
    api.get("/admin/bookings", { params }).then(({ data }) => setBookings(data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const validate = async (b) => {
    await api.post(`/admin/payments/${b.id}/validate`).catch(() => {});
    toast.success("Paiement validé");
    load();
  };
  const cancel = async (b) => {
    await api.post(`/admin/payments/${b.id}/cancel`).catch(() => {});
    toast.success("Réservation annulée");
    load();
  };

  return (
    <div>
      <h2 className="font-head text-2xl font-extrabold text-primary mb-4">Réservations & paiements</h2>
      <div className="flex gap-2 mb-5 overflow-x-auto">
        {STATUSES.map((s) => (
          <button key={s.key} onClick={() => setFilter(s.key)} data-testid={`filter-${s.key || "all"}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${filter === s.key ? "bg-primary text-white" : "bg-white border border-gray-200 text-primary"}`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {bookings.length === 0 && <p className="text-muted-foreground text-sm">Aucune réservation.</p>}
        {bookings.map((b) => (
          <div key={b.id} className="bg-white rounded-2xl soft-shadow border border-gray-100 p-4" data-testid={`admin-booking-${b.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${BADGE[b.status]}`}>{LABEL[b.status]}</span>
                  {b.payment_method && <span className="text-xs text-muted-foreground">{b.payment_method === "campay" ? "Mobile Money" : "Manuel"}</span>}
                </div>
                <h3 className="font-semibold text-primary">{b.offer_title}</h3>
                <p className="text-sm text-muted-foreground">{b.user_name} · {b.user_email}</p>
                <p className="text-sm text-muted-foreground">
                  {b.destination}{b.departure_country ? ` · Départ depuis ${b.departure_country}` : ""}
                </p>
                <p className="text-sm text-muted-foreground">
                  {new Date(b.depart_date || b.date).toLocaleDateString("fr-FR")}{b.return_date ? ` → ${new Date(b.return_date).toLocaleDateString("fr-FR")}` : ""} · {b.num_people} pers.
                </p>
                {b.manual_tx_id && (
                  <p className="text-sm text-primary mt-1">Transaction : <span className="font-mono">{b.manual_tx_id}</span>
                    {b.manual_screenshot_file_id && (
                      <a href={`${API}/files/${b.manual_screenshot_file_id}/download?auth=${getToken()}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 ml-2 text-accent font-semibold" data-testid={`view-proof-${b.id}`}>
                        <Download className="h-3.5 w-3.5" /> Capture
                      </a>
                    )}
                  </p>
                )}
              </div>
              <div className="text-right">
                <div className="font-head text-xl font-extrabold text-accent">{fcfa(b.total_amount)}</div>
                {b.status !== "payee" && (
                  <div className="flex gap-1 mt-2 justify-end">
                    <Button size="sm" onClick={() => validate(b)} className="rounded-full bg-green-600 hover:bg-green-700 text-white" data-testid={`validate-${b.id}`}>
                      <Check className="h-4 w-4 mr-1" /> Valider
                    </Button>
                    {b.status !== "annulee" && (
                      <Button size="sm" variant="ghost" onClick={() => cancel(b)} className="rounded-full text-destructive" data-testid={`cancel-${b.id}`}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
